// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "15.5.18";import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

// nextjs initialization of client-side posthog: https://posthog.com/docs/libraries/next-js
posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
  api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  // this constant is from the docs
  defaults: "2025-05-24",
  disable_session_recording: true,
});

// matches synthetic events produced by Sentry's httpClientIntegration when Clerk's
// frontend API hiccups (5xx). the Clerk SDK already retries these internally and
// our existing `ignoreErrors` regex only catches the SDK-thrown variant, not the
// auto-captured fetch event whose url lives on `event.request.url`.
const CLERK_FRONTEND_API_URL_PATTERN = /^https:\/\/clerk\.(?:builtright\.co|rebolthq\.com)\/v1\/client\//;

// segment's analytics-next fetches its settings from cdn.segment.com on boot and
// sends events to api.segment.io. transient 5xx from those endpoints are
// third-party noise we don't control, but httpClientIntegration auto-captures them
// the same way it does the Clerk hiccups above. url lives on `event.request.url`.
const SEGMENT_CDN_URL_PATTERN = /^https:\/\/(?:cdn\.segment\.com|api\.segment\.io)\//;

Sentry.init({
  ignoreErrors: [
    "Non-Error exception captured",
    "Non-Error promise rejection captured",
    /Network error at "https:\/\/clerk\.(?:builtright\.co|rebolthq\.com)\/v1\/client/,
    // cal.com embed script races its own iframe setup on the marketing homepage
    "createIframe must be called before doInIframe",
    // fullstory analytics script crashes in bot/crawler browsers on /terms
    "TrackerStorageType is not defined",
    // next.js router throws this when a link/redirect targets the current url; harmless.
    "Invariant: attempted to hard navigate to the same URL",
    // ios in-app webviews (instagram/facebook) inject a bridge probe; our code never touches it.
    "window.webkit.messageHandlers",
    // crypto-wallet browser extensions inject window.ethereum and throw on pages that never use it.
    "window.ethereum",
  ],
  beforeSend(event) {
    const exception = event.exception?.values?.[0];
    const requestUrl = event.request?.url;
    if (
      exception?.mechanism?.type === "auto.http.client.fetch" &&
      typeof requestUrl === "string" &&
      (CLERK_FRONTEND_API_URL_PATTERN.test(requestUrl) ||
        SEGMENT_CDN_URL_PATTERN.test(requestUrl))
    ) {
      return null;
    }

    // monaco editor rejects its in-flight requests with a bare "Canceled" error
    // when it disposes (e.g. navigating away mid-completion). scope strictly to
    // monaco stack frames so we never swallow unrelated "Canceled"/abort errors.
    if (
      exception?.type === "Canceled" &&
      exception.value === "Canceled" &&
      exception.stacktrace?.frames?.some(
        (frame) => typeof frame.filename === "string" && frame.filename.includes("monaco-editor")
      )
    ) {
      return null;
    }

    // a third-party widget at /embed/embed.js on our marketing pages calls
    // getComputedStyle on a not-yet-mounted element inside a setInterval, mostly
    // in headless/bot browsers. it isn't our code to fix. scope strictly to the
    // embed.js frame so our own getComputedStyle calls still report. (BUILTRIGHTCO-4K5)
    if (
      exception?.type === "TypeError" &&
      typeof exception.value === "string" &&
      exception.value.includes("getComputedStyle") &&
      exception.stacktrace?.frames?.some(
        (frame) => typeof frame.filename === "string" && frame.filename.includes("/embed/embed.js")
      )
    ) {
      return null;
    }

    return event;
  },
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  enabled:
    process.env.NODE_ENV === "production" ||
    process.env.VERCEL_ENV === "preview" ||
    process.env.NEXT_PUBLIC_FORCE_ENABLE_SENTRY === "true",

  release: process.env.SENTRY_RELEASE || process.env.VERCEL_GIT_COMMIT_SHA,

  normalizeDepth: 6,

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 0.15,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  replaysOnErrorSampleRate: process.env.NODE_ENV === "production" ? 1.0 : 0,
  replaysSessionSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 0,

  // You can remove this option if you're not planning to use the Sentry Session Replay feature:
  integrations: [
    Sentry.replayIntegration({
      // Additional Replay configuration goes in here, for example:
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [/api\/.*/],
      networkRequestHeaders: ["X-Clerk-Auth-Status"],
      mask: ["cl-formFieldInput__password"],
    }),
    // [[500, 599]] is the SDK default — stated explicitly to document that
    // this integration's scope is 5xx-only and must stay that way. It never
    // captured 4xx (so it is not the source of historical 401 noise; that was
    // handleApiRequest → logFrontendError). Persistent 401s are covered by the
    // interceptor's auth-recovery-failed signal instead.
    Sentry.httpClientIntegration({ failedRequestStatusCodes: [[500, 599]] }),
  ],
  // This option is required for capturing headers and cookies.
  sendDefaultPii: true,
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
